import React from "react";
import { Box, Paper, Skeleton, TableContainer, Typography } from "@mui/material";
import DepartmentDatePicker from "../DepartmentReport/DepartmentDatePicker";
import dayjs from "dayjs";

const DepartmentDailyGrid = ({ departmentReportData, departmentStartDate, departmentEndDate, loading = false, onDepartmentDateChange }) => {
  const dateCols = [];
  if (departmentStartDate && departmentEndDate) {
    let currentDate = dayjs(departmentStartDate);
    while (currentDate.isBefore(departmentEndDate) || currentDate.isSame(departmentEndDate)) {
      dateCols.push(currentDate.format("DD-MM-YYYY"));
      currentDate = currentDate.add(1, "day");
    }
  }

  const statuses = [
    { key: "Unassigned", source: "unassigned" },
    { key: "Assigned", source: "assigned" },
    { key: "Reassigned", source: "reassigned" },
    { key: "Completed", source: "completed" },
  ];

  const colorFor = (status) => {
    switch (status) {
      case "Assigned":
        return "#2563eb";
      case "Reassigned":
        return "#ea580c";
      case "Completed":
        return "#16a34a";
      default:
        return "#6b7280";
    }
  };

  const rows = statuses.map((s) => {
    const values = dateCols.map((d) => {
      const dateData = departmentReportData?.find((x) => x._id === d);
      return dateData ? dateData[s.source] || 0 : 0;
    });
    const total = values.reduce((sum, n) => sum + n, 0);
    return { description: s.key, values, total };
  });

  if (loading) {
    const cols = 8;
    return (
      <Paper elevation={1} sx={{ overflow: "hidden", borderRadius: 2, border: "1px solid", borderColor: "divider" }} className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
        <Box sx={{ position: "relative", background: "linear-gradient(90deg, rgba(239,246,255,1) 0%, rgba(219,234,254,1) 100%)", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2, minHeight: 56 }}>
          <Skeleton variant="text" width={200} height={28} />
          <Box sx={{ position: "absolute", right: 12, top: 10 }}>
            <Skeleton variant="rounded" width={260} height={36} />
          </Box>
        </Box>
        <TableContainer>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb" }}>
                <th style={{ width: 223, padding: "8px 12px", borderRight: "1px solid #e5e7eb" }}>
                  <Skeleton variant="text" width={90} height={16} />
                </th>
                {[...Array(cols)].map((_, i) => (
                  <th key={i} style={{ padding: 8, borderRight: i < cols - 1 ? "1px solid #e5e7eb" : "none" }}>
                    <Skeleton variant="text" width={80} height={16} />
                  </th>
                ))}
                <th style={{ width: 94, padding: "8px 12px", borderRight: "1px solid #e5e7eb" }}>
                  <Skeleton variant="text" width={50} height={16} />
                </th>
              </tr>
            </thead>
            <tbody>
              {["Unassigned", "Assigned", "Reassigned", "Completed"].map((label) => (
                <tr key={label}>
                  <td style={{ padding: 12, borderRight: "1px solid #f3f4f6", borderTop: "1px solid #e5e7eb" }}>
                    <Skeleton variant="text" width={90} height={16} />
                  </td>
                  {[...Array(cols)].map((_, i) => (
                    <td key={i} style={{ padding: 12, borderRight: i < cols - 1 ? "1px solid #f3f4f6" : "none", borderTop: "1px solid #e5e7eb" }}>
                      <Skeleton variant="text" width={20} height={16} />
                    </td>
                  ))}
                  <td style={{ padding: 12, borderTop: "1px solid #e5e7eb" }}>
                    <Skeleton variant="text" width={30} height={16} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ overflow: "hidden", borderRadius: 2, border: "1px solid", borderColor: "divider" }} className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
      <Box sx={{ position: "relative", background: "linear-gradient(90deg, rgba(239,246,255,1) 0%, rgba(219,234,254,1) 100%)", borderBottom: "1px solid", borderColor: "divider", px: 3, py: 2, minHeight: 56 }}>
        <Typography variant="h6" sx={{fontFamily: "Inter", color: "grey.800", fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.75 }}>
          Comprehensive Data
        </Typography>
        <Box sx={{ position: "absolute", right: 12, top: 10 }}>
          <DepartmentDatePicker
            value={[departmentStartDate, departmentEndDate]}
            onChange={onDepartmentDateChange}
          />
        </Box>
      </Box>
      <TableContainer>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <th style={{ width: 223, fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", borderRight: "1px solid #e5e7eb", padding: "8px 12px", textAlign: "left" }}>DESCRIPTION</th>
              {dateCols.map((d, idx) => (
                <th key={d} style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", borderRight: idx < dateCols.length ? "1px solid #e5e7eb" : "none", padding: 8, textAlign: "center", whiteSpace: "nowrap" }}>{d}</th>
              ))}
              <th style={{ width: 94, fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", padding: "8px 12px", textAlign: "center" }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.description} style={{ backgroundColor: "#fff", borderTop: "1px solid #e5e7eb" }}>
                <td style={{ fontSize: 14, color: "#111827", lineHeight: "20px", borderRight: "1px solid #f3f4f6", padding: index === 0 ? 8 : 12 }}>{row.description}</td>
                {row.values.map((v, i) => (
                  <td key={i} style={{ textAlign: "center", fontSize: 14, color: colorFor(row.description), borderRight: i < row.values.length - 1 ? "1px solid #f3f4f6" : "none", padding: index === 0 ? 8 : 12 }}>{v}</td>
                ))}
                <td style={{ textAlign: "center", fontSize: 14, color: "#111827", padding: index === 0 ? 8 : 12, fontWeight: 600 }}>{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableContainer>
    </Paper>
  );
};

export default DepartmentDailyGrid;


